import React, { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, ScanLine, Brain, Droplets, Compass, ArrowRight, Loader2, ImageIcon, Save } from "lucide-react";

const likelihoodColor = {
    high: "bg-ares-cyan/15 text-ares-cyan border-ares-cyan/40",
    moderate: "bg-ares-amber/15 text-ares-amber border-ares-amber/40",
    low: "bg-ares-muted/15 text-ares-muted border-ares-muted/40",
    none: "bg-ares-muted/15 text-ares-muted border-ares-muted/40",
    unknown: "bg-ares-muted/15 text-ares-muted border-ares-muted/40",
};

export default function AiClassifier({ onSaved }) {
    const [preview, setPreview] = useState(null);
    const [file, setFile] = useState(null);
    const [context, setContext] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [projects, setProjects] = useState([]);
    const [saveProject, setSaveProject] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    }, [result]);

    const pickFile = (f) => {
        if (!f) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
            toast.error("Please upload a JPEG, PNG or WEBP image.");
            return;
        }
        setFile(f);
        setResult(null);
        setPreview(URL.createObjectURL(f));
    };

    const onDrop = (e) => {
        e.preventDefault();
        pickFile(e.dataTransfer.files?.[0]);
    };

    const classify = async () => {
        if (!file) return;
        setLoading(true);
        setResult(null);
        const form = new FormData();
        form.append("file", file);
        form.append("context", context);
        try {
            const { data } = await api.post("/classify", form, { headers: { "Content-Type": "multipart/form-data" } });
            setResult(data);
            toast.success("Terrain analysis complete");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Analysis failed");
        } finally {
            setLoading(false);
        }
    };

    const saveToProject = async () => {
        if (!saveProject || !result) return;
        const summary = `AI Classification — ${result.summary} | Ice likelihood: ${result.water_ice_likelihood}. Features: ${(result.features || []).map((f) => `${f.name} (${f.confidence}%)`).join(", ")}`;
        try {
            await api.post(`/projects/${saveProject}/findings`, { text: summary, feature_type: "ai-classification" });
            toast.success("Saved to project workspace");
            onSaved && onSaved();
        } catch (e) {
            toast.error("Could not save finding");
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Upload / preview */}
            <div className="rounded-xl border border-ares-border bg-ares-dark/90 p-5">
                <div className="flex items-center gap-2 mb-4">
                    <Brain className="w-5 h-5 text-ares-emerald" />
                    <h2 className="font-heading text-lg font-medium">AI Feature Classifier</h2>
                </div>

                <div
                    onDrop={onDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => inputRef.current?.click()}
                    className="relative rounded-lg border-2 border-dashed border-ares-border hover:border-ares-emerald/50 bg-ares-panel/50 cursor-pointer transition-colors overflow-hidden"
                    data-testid="ai-image-dropzone"
                    style={{ minHeight: 280 }}
                >
                    {preview ? (
                        <>
                            <img src={preview} alt="Upload preview" className="w-full h-[280px] object-cover" />
                            {loading && <span className="scan-line" />}
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-[280px] text-center px-6">
                            <div className="w-12 h-12 rounded-full bg-ares-emerald/10 border border-ares-emerald/30 flex items-center justify-center mb-3">
                                <Upload className="w-5 h-5 text-ares-emerald" />
                            </div>
                            <p className="text-sm font-medium">Drop a Mars orbital image here</p>
                            <p className="text-xs text-ares-muted mt-1">or click to browse · JPEG, PNG, WEBP</p>
                        </div>
                    )}
                    <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                        onChange={(e) => pickFile(e.target.files?.[0])} data-testid="ai-image-upload-input" />
                </div>

                <Textarea
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="Optional context (e.g. 'HiRISE image of northern plains, ~25cm/px')"
                    className="mt-4 bg-ares-panel border-ares-border resize-none h-20 text-sm"
                    data-testid="ai-context-input"
                />

                <Button onClick={classify} disabled={!file || loading}
                    className="w-full mt-4 h-11 bg-ares-emerald hover:bg-[#00cc6e] text-ares-void font-semibold" data-testid="ai-classify-submit-btn">
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Scanning terrain…</> : <><ScanLine className="w-4 h-4 mr-2" /> Run Classification</>}
                </Button>
            </div>

            {/* Results */}
            <div className="rounded-xl border border-ares-border bg-ares-dark/90 p-5" data-testid="ai-results-panel">
                <h3 className="font-mono text-[10px] uppercase tracking-widest text-ares-muted mb-4">Analysis Output</h3>

                {!result && !loading && (
                    <div className="flex flex-col items-center justify-center h-[300px] text-center text-ares-muted">
                        <ImageIcon className="w-10 h-10 mb-3 opacity-40" />
                        <p className="text-sm">Upload an image and run the classifier to see detected geological features.</p>
                    </div>
                )}

                {loading && (
                    <div className="flex flex-col items-center justify-center h-[300px] text-center">
                        <Loader2 className="w-8 h-8 text-ares-emerald animate-spin mb-3" />
                        <p className="font-mono text-xs uppercase tracking-widest text-ares-emerald">Processing spectral features…</p>
                    </div>
                )}

                {result && (
                    <div className="space-y-5 fade-up">
                        <p className="text-sm text-ares-bright/90 leading-relaxed">{result.summary}</p>

                        <div className="flex flex-wrap gap-2">
                            <Badge className={`border ${likelihoodColor[result.water_ice_likelihood] || likelihoodColor.unknown}`}>
                                <Droplets className="w-3 h-3 mr-1" /> Ice: {result.water_ice_likelihood}
                            </Badge>
                        </div>

                        {(result.features || []).length > 0 && (
                            <div className="space-y-2.5">
                                <p className="font-mono text-[10px] uppercase tracking-widest text-ares-muted">Detected features</p>
                                {result.features.map((f, i) => (
                                    <div key={i} className="rounded-lg border border-ares-border bg-ares-panel p-3" data-testid={`ai-feature-${i}`}>
                                        <div className="flex items-center justify-between mb-1.5">
                                            <span className="text-sm font-medium capitalize">{f.name}</span>
                                            <span className="font-mono text-xs text-ares-emerald">{f.confidence}%</span>
                                        </div>
                                        <div className="h-1.5 rounded-full bg-ares-void overflow-hidden">
                                            <div className="h-full bg-ares-emerald rounded-full" style={{ width: `${f.confidence}%` }} />
                                        </div>
                                        {f.note && <p className="text-xs text-ares-muted mt-1.5">{f.note}</p>}
                                    </div>
                                ))}
                            </div>
                        )}

                        {result.terrain_suitability && (
                            <div className="flex items-start gap-2 text-sm text-ares-muted">
                                <Compass className="w-4 h-4 text-ares-orange mt-0.5 shrink-0" />
                                <span><span className="text-ares-bright">Traversal: </span>{result.terrain_suitability}</span>
                            </div>
                        )}
                        {result.recommended_followup && (
                            <div className="flex items-start gap-2 text-sm text-ares-muted">
                                <ArrowRight className="w-4 h-4 text-ares-cyan mt-0.5 shrink-0" />
                                <span><span className="text-ares-bright">Next step: </span>{result.recommended_followup}</span>
                            </div>
                        )}

                        {projects.length > 0 && (
                            <div className="border-t border-ares-border pt-4 flex gap-2">
                                <Select value={saveProject} onValueChange={setSaveProject}>
                                    <SelectTrigger className="bg-ares-panel border-ares-border flex-1" data-testid="ai-save-project-select">
                                        <SelectValue placeholder="Save to project…" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-ares-panel border-ares-border">
                                        {projects.map((p) => (
                                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button onClick={saveToProject} disabled={!saveProject} className="bg-ares-orange hover:bg-[#e55a2b] text-white" data-testid="ai-save-finding-btn">
                                    <Save className="w-4 h-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
